import { Button } from "@msuite/ui/button";
import { useLoading } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Menu } from "@msuite/ui/menu";
import { RowActionsWrapper, useDataTableContext } from "@msuite/ui/table";
import { responseHandler } from "@msuite/ui/utils";
import { getPartnershipRole } from "@msuite/utils/partnerships";
import type { OrganizationPartnership } from "@msuite/utils/types";
import { useNavigate } from "react-router";
import { client } from "@/hono";
import { useAuth } from "@/hooks/use-auth";
import { useCitadel } from "@/hooks/use-citadel";

/** Props Interface */
interface RowActionsProps {
	partner: OrganizationPartnership;
}

export function RowActions({ partner }: RowActionsProps) {
	/** Hooks */
	const context = useDataTableContext();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const loader = useLoading();
	const { organization } = useAuth();

	/** Query */
	const { citadel } = useCitadel();

	/** Functions */
	function handleOnNavigateToPartner() {
		navigate(`/partners/${partner._partner.id}`);
	}

	async function handleOnChangePartnershipState() {
		try {
			if (!partner) return;
			loader.start();
			const response = await client.organizations[":orgId"].partnerships[
				":partnershipId"
			].state.$patch({
				param: {
					orgId: partner.hostOrgId,
					partnershipId: partner.id,
				},
				json: {
					state: partner.state === "active" ? "inactive" : "active",
				},
			});
			await responseHandler(response);
			await context.refetch?.();
		} catch (error) {
			// `responseHandler` has already toasted the reason; swallowing the throw
			// without a trace would leave the row looking like it simply did nothing.
			console.error(error);
		} finally {
			loader.stop();
		}
	}

	/** Computed */
	// `PATCH /partnerships/:partnershipId/state` gates on `update Partner`, so the
	// button only offers what the server would accept. The three-state guard the
	// `Can` component documents applies: while the rules load, and after a failed
	// refetch with no cached rules, the button stays and the server decides.
	const partnerUpdateIsDenied = !!citadel && citadel.cannot("update", "Partner");
	// Only the host of a row may change its state, so a row the counterpart hosts offers no button.
	const viewerHostsPartnership = getPartnershipRole(organization.id, partner) === "host";

	/** Render */
	return (
		<RowActionsWrapper to={`/partners/${partner._partner.id}`}>
			{partner.state !== "active" && viewerHostsPartnership && !partnerUpdateIsDenied && (
				<Button
					onClick={handleOnChangePartnershipState}
					variant={"white"}
					isLoading={loader.isLoading}
				>
					{t("organization.activate_partnership")}
				</Button>
			)}
			<Menu
				items={() => [
					{
						label: t("general.open"),
						icon: icon("Info"),
						onClick: handleOnNavigateToPartner,
						cyId: "row-actions-open-button",
					},
				]}
			>
				<Button
					size="xxs"
					icon={<Icon name="EllipsisVertical" />}
					variant="ghost"
					data-cy="row-actions-button"
				/>
			</Menu>
		</RowActionsWrapper>
	);
}
